import React, { useCallback, useEffect, useRef, useState } from 'react';
import './FlippyBird.css';

const W = 288, H = 512, FLOOR = 454;
const BIRD_X = 78, BIRD_R = 11;
const GRAVITY = 780, FLAP = -255, PIPE_SPEED = 118;
const PIPE_W = 48, PIPE_INTERVAL = 1.47;
const BEST_KEY = 'hobbli-flap-best-v1'; // Preserve existing local best scores

function randomGap(score) {
  const gap = Math.max(116, 150 - Math.floor(score / 7) * 5);
  const center = 115 + Math.random() * 205;
  return { top: Math.max(55, Math.min(FLOOR - gap - 55, center - gap / 2)), gap };
}
function rectHitCircle(x, y, r, rx, ry, rw, rh) {
  const cx = Math.max(rx, Math.min(x, rx + rw));
  const cy = Math.max(ry, Math.min(y, ry + rh));
  return (x - cx) ** 2 + (y - cy) ** 2 < r * r;
}
function pixelText(ctx, str, x, y, size = 22, align = 'center') {
  ctx.font = `bold ${size}px monospace`;
  ctx.textAlign = align;
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#49382d';
  ctx.strokeText(String(str), x, y);
  ctx.fillStyle = '#fff7dc';
  ctx.fillText(String(str), x, y);
}
function drawCloud(ctx, x, y) {
  ctx.fillStyle = '#e7faff';
  ctx.fillRect(x + 8, y, 24, 8);
  ctx.fillRect(x, y + 8, 48, 13);
  ctx.fillRect(x + 8, y + 21, 32, 5);
  ctx.fillStyle = '#fff';
  ctx.fillRect(x + 10, y + 4, 20, 8);
  ctx.fillRect(x + 4, y + 12, 40, 8);
}
function drawPipe(ctx, x, top, gap) {
  const bottom = top + gap;
  function section(y, h, lipY) {
    ctx.fillStyle = '#275d21'; ctx.fillRect(x, y, PIPE_W, h);
    ctx.fillStyle = '#64c83a'; ctx.fillRect(x + 4, y, PIPE_W - 9, h);
    ctx.fillStyle = '#a0ec54'; ctx.fillRect(x + 8, y, 7, h);
    ctx.fillStyle = '#37892a'; ctx.fillRect(x + PIPE_W - 12, y, 6, h);
    ctx.fillStyle = '#204a20'; ctx.fillRect(x - 4, lipY, PIPE_W + 8, 19);
    ctx.fillStyle = '#66c43d'; ctx.fillRect(x - 1, lipY + 3, PIPE_W + 2, 13);
    ctx.fillStyle = '#a8f06c'; ctx.fillRect(x + 4, lipY + 5, 7, 9);
    ctx.fillStyle = '#337c2b'; ctx.fillRect(x + PIPE_W - 9, lipY + 3, 5, 13);
  }
  section(0, top - 18, top - 19);
  section(bottom + 19, FLOOR - bottom - 19, bottom);
}
function drawBird(ctx, y, wing, tilt) {
  ctx.save(); ctx.translate(BIRD_X, Math.round(y)); ctx.rotate(tilt);
  ctx.fillStyle = '#533b2b'; ctx.fillRect(-12, -9, 24, 19);
  ctx.fillStyle = '#ffd746'; ctx.fillRect(-10, -10, 20, 18);
  ctx.fillStyle = '#fff3a1'; ctx.fillRect(-7, 1, 15, 7);
  ctx.fillStyle = '#f8a536'; ctx.fillRect(-8, wing ? 0 : -5, 13, 8);
  ctx.fillStyle = '#f9c453'; ctx.fillRect(-6, wing ? 1 : -4, 9, 4);
  ctx.fillStyle = '#4c3325'; ctx.fillRect(5, -7, 8, 9);
  ctx.fillStyle = '#fff'; ctx.fillRect(6, -6, 6, 7);
  ctx.fillStyle = '#1d1d1d'; ctx.fillRect(9, -4, 3, 4);
  ctx.fillStyle = '#b75b2b'; ctx.fillRect(10, 3, 13, 6);
  ctx.fillStyle = '#ff9b2e'; ctx.fillRect(12, 3, 11, 4);
  ctx.restore();
}
function mixColor(a, b, t) {
  const c = (v, i) => parseInt(v.slice(i, i + 2), 16);
  const n = (x, y) => Math.round(x + (y - x) * t);
  return `rgb(${n(c(a, 1), c(b, 1))},${n(c(a, 3), c(b, 3))},${n(c(a, 5), c(b, 5))})`;
}
function drawBackdrop(ctx, score, time) {
  // Smoothly crossfade palettes around score milestones, without changing gameplay.
  const stages = [
    { at: 0, sky: '#78d6f4', far: '#c6edab', near: '#8bc77a' },
    { at: 15, sky: '#f4a16d', far: '#df9d9b', near: '#9876a5' },
    { at: 30, sky: '#7661a9', far: '#68568c', near: '#423d73' },
    { at: 50, sky: '#182849', far: '#2d335b', near: '#202644' }
  ];
  let i = 0;
  while (i < stages.length - 1 && score >= stages[i + 1].at) i++;
  const from = stages[i], to = stages[Math.min(i + 1, stages.length - 1)];
  const t = i === stages.length - 1 ? 0 : Math.max(0, Math.min(1, (score - (to.at - 3)) / 3));
  const sky = mixColor(from.sky, to.sky, t);
  const far = mixColor(from.far, to.far, t);
  const near = mixColor(from.near, to.near, t);
  ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);
  const city = score >= 30;
  if (score >= 50) {
    ctx.fillStyle = '#dfe8ff';
    for (let j = 0; j < 28; j++) {
      const x = (j * 79 + 31) % W, y = (j * 47 + 13) % 260;
      ctx.fillRect(x, y, j % 5 === 0 ? 2 : 1, j % 5 === 0 ? 2 : 1);
    }
  } else {
    for (let j = 0; j < 5; j++) {
      const x = ((j * 96 - time * 13) % (W + 100) + W + 100) % (W + 100) - 50;
      drawCloud(ctx, Math.round(x), 45 + (j % 3) * 65);
    }
  }
  if (city) {
    const farOffset = Math.floor(time * 9) % 42;
    ctx.fillStyle = far;
    for (let j = -1; j < 10; j++) {
      const height = 75 + ((j * 37 + 200) % 72);
      ctx.fillRect(j * 42 - farOffset, FLOOR - height, 35, height);
    }
    const nearOffset = Math.floor(time * 17) % 54;
    ctx.fillStyle = near;
    for (let j = -1; j < 8; j++) {
      const h = 48 + ((j * 53 + 300) % 75);
      const x = j * 54 - nearOffset, y = FLOOR - h;
      ctx.fillRect(x, y, 47, h);
      ctx.fillStyle = score >= 50 ? '#f9d679' : '#f4c2a0';
      for (let wy = y + 10; wy < FLOOR - 8; wy += 13) {
        for (let wx = x + 8; wx < x + 43; wx += 13) {
          if ((j + wx + wy) % 4 !== 0) ctx.fillRect(wx, wy, 5, 6);
        }
      }
      ctx.fillStyle = near;
    }
    // A rare, purely decorative Hobbli easter egg in the distant city.
    // It scrolls behind pipes and never changes obstacle hitboxes.
    if (score >= 35 && score % 20 < 10) {
      const billboardX = W - ((time * 13) % (W + 150));
      const x = Math.round(billboardX);
      ctx.fillStyle = '#171a36'; ctx.fillRect(x, 277, 93, 33);
      ctx.fillStyle = '#e75da5'; ctx.fillRect(x + 2, 279, 89, 29);
      ctx.fillStyle = '#171a36'; ctx.fillRect(x + 5, 282, 83, 23);
      ctx.fillStyle = '#fff0a2'; ctx.font = 'bold 13px monospace';
      ctx.textAlign = 'center'; ctx.fillText('HOBBLI', x + 46, 298);
    }
  } else {
    ctx.fillStyle = far;
    for (let x = -30; x < W + 40; x += 32) {
      const off = (time * 20) % 32;
      ctx.fillRect(Math.round(x - off), 392 + (Math.floor(x / 32) % 3) * 5, 28, 63);
    }
    ctx.fillStyle = near; ctx.fillRect(0, 427, W, 27);
  }
}
function drawScene(ctx, game, time) {
  ctx.imageSmoothingEnabled = false;
  drawBackdrop(ctx, game.score, time);
  for (const pipe of game.pipes) drawPipe(ctx, Math.round(pipe.x), Math.round(pipe.top), pipe.gap);
  ctx.fillStyle = '#4d8c3b'; ctx.fillRect(0, FLOOR - 5, W, 5);
  ctx.fillStyle = '#e9d68a'; ctx.fillRect(0, FLOOR, W, H - FLOOR);
  ctx.fillStyle = '#d6bb6e';
  for (let x = -20; x < W + 30; x += 22) {
    const shift = (time * PIPE_SPEED * .7) % 22;
    ctx.fillRect(Math.round(x - shift), FLOOR + 10, 13, 3);
    ctx.fillRect(Math.round(x - shift + 8), FLOOR + 30, 13, 3);
  }
  ctx.fillStyle = '#7fbb45'; ctx.fillRect(0, FLOOR, W, 6);
  drawBird(ctx, game.y, Math.floor(time * 11) % 2, Math.max(-.35, Math.min(1.0, game.vy / 430)));
  if (game.phase === 'playing') pixelText(ctx, game.score, W / 2, 54, 29);
  if (game.phase === 'ready') {
    pixelText(ctx, 'FLIPPY BIRD', W / 2, 103, 23);
    pixelText(ctx, 'TAP TO FLAP', W / 2, 163, 17);
    pixelText(ctx, 'AVOID THE PIPES!', W / 2, 190, 12);
  }
  if (game.phase === 'over') {
    ctx.fillStyle = 'rgba(35,48,47,.60)'; ctx.fillRect(22, 122, W - 44, 207);
    ctx.strokeStyle = '#fff2b1'; ctx.lineWidth = 4; ctx.strokeRect(24, 124, W - 48, 203);
    pixelText(ctx, 'GAME OVER', W / 2, 164, 23);
    pixelText(ctx, `SCORE  ${game.score}`, W / 2, 210, 18);
    pixelText(ctx, `BEST   ${game.best}`, W / 2, 241, 18);
    pixelText(ctx, 'TAP TO RETRY', W / 2, 291, 15);
  }
  if (game.phase === 'paused') {
    ctx.fillStyle = 'rgba(30,40,40,.50)'; ctx.fillRect(0, 0, W, FLOOR);
    pixelText(ctx, 'PAUSED', W / 2, 213, 29);
    pixelText(ctx, 'TAP TO RESUME', W / 2, 250, 15);
  }
}
function freshGame(best = 0) {
  return { phase: 'ready', y: 220, vy: 0, pipes: [], elapsed: 0, sincePipe: 0,
    score: 0, best, time: 0 };
}
export default function FlippyBird({ onGameOver }) {
  const canvasRef = useRef(null);
  const gameRef = useRef(freshGame());
  const callbackRef = useRef(onGameOver);
  const [hud, setHud] = useState({ phase: 'ready', score: 0, best: 0 });
  const [muted, setMuted] = useState(false);
  useEffect(() => { callbackRef.current = onGameOver; }, [onGameOver]);
  useEffect(() => {
    try {
      const best = Math.max(0, Number(localStorage.getItem(BEST_KEY)) || 0);
      gameRef.current.best = best;
      setHud(h => ({ ...h, best }));
    } catch { /* storage may be disabled */ }
  }, []);
  const updateHud = useCallback(() => {
    const g = gameRef.current;
    setHud({ phase: g.phase, score: g.score, best: g.best });
  }, []);
  const flap = useCallback(() => {
    const g = gameRef.current;
    if (g.phase === 'over') {
      gameRef.current = freshGame(g.best);
      gameRef.current.phase = 'playing';
      gameRef.current.vy = FLAP;
    } else if (g.phase === 'ready') {
      g.phase = 'playing'; g.vy = FLAP;
    } else if (g.phase === 'paused') {
      g.phase = 'playing';
    } else if (g.phase === 'playing') {
      g.vy = FLAP;
    }
    updateHud();
  }, [updateHud]);
  const pause = useCallback(() => {
    const g = gameRef.current;
    if (g.phase === 'playing') g.phase = 'paused';
    else if (g.phase === 'paused') g.phase = 'playing';
    updateHud();
  }, [updateHud]);
  useEffect(() => {
    const onKey = (event) => {
      if (event.code === 'Space' || event.code === 'ArrowUp') {
        if (event.repeat) return;
        event.preventDefault(); flap();
      } else if (event.code === 'KeyP' || event.code === 'Escape') {
        event.preventDefault(); pause();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flap, pause]);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    let raf = 0, last = 0;
    function endGame(g) {
      if (g.phase !== 'playing') return;
      g.phase = 'over';
      if (g.score > g.best) {
        g.best = g.score;
        try { localStorage.setItem(BEST_KEY, String(g.best)); } catch { /* optional */ }
      }
      updateHud();
      if (callbackRef.current) {
        try { callbackRef.current({ score: g.score, best: g.best }); }
        catch (error) { console.error('FlippyBird onGameOver callback failed', error); }
      }
    }
    function frame(ts) {
      if (!last) last = ts;
      const dt = Math.min((ts - last) / 1000, .035);
      last = ts;
      const g = gameRef.current;
      if (g.phase !== 'paused') g.time += dt;
      if (g.phase === 'ready') g.y = 220 + Math.sin(g.time * 3.5) * 5;
      if (g.phase === 'playing') {
        g.elapsed += dt;
        g.sincePipe += dt;
        g.vy += GRAVITY * dt;
        g.y += g.vy * dt;
        const speed = PIPE_SPEED + Math.min(40, Math.floor(g.score / 5) * 5);
        if (g.sincePipe >= PIPE_INTERVAL) {
          g.sincePipe -= PIPE_INTERVAL;
          g.pipes.push({ x: W + 8, ...randomGap(g.score), scored: false });
        }
        for (const pipe of g.pipes) {
          pipe.x -= speed * dt;
          if (!pipe.scored && pipe.x + PIPE_W < BIRD_X - BIRD_R) {
            pipe.scored = true; g.score++; updateHud();
          }
          if (rectHitCircle(BIRD_X, g.y, BIRD_R - 2, pipe.x - 4, 0, PIPE_W + 8, pipe.top - 1) ||
              rectHitCircle(BIRD_X, g.y, BIRD_R - 2, pipe.x - 4, pipe.top + pipe.gap, PIPE_W + 8, FLOOR - pipe.top - pipe.gap)) {
            endGame(g); break;
          }
        }
        g.pipes = g.pipes.filter(p => p.x + PIPE_W > -10);
        if (g.y + BIRD_R >= FLOOR || g.y - BIRD_R <= 0) endGame(g);
      }
      drawScene(ctx, g, g.time);
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [updateHud]);
  return (
    <section className="flippy-bird" aria-label="Flippy Bird arcade game">
      <div className="flippy-bird__header">
        <span className="flippy-bird__brand">FLIPPY <strong>BIRD</strong></span>
        <span className="flippy-bird__best">BEST: {hud.best}</span>
      </div>
      <div className="flippy-bird__screen">
        <canvas ref={canvasRef} width={W} height={H} tabIndex={0}
          aria-label="Flappy arcade game. Tap, click or press space to flap."
          onPointerDown={(event) => { event.preventDefault(); event.currentTarget.focus(); flap(); }} />
      </div>
      <div className="flippy-bird__controls">
        <button type="button" onClick={pause} disabled={hud.phase === 'ready' || hud.phase === 'over'}>
          {hud.phase === 'paused' ? 'RESUME' : 'PAUSE'}
        </button>
        <span>CLICK / TAP / SPACE TO FLAP</span>
        <button type="button" onClick={() => { gameRef.current = freshGame(gameRef.current.best); updateHud(); }}>
          RESET
        </button>
      </div>
      <p className="flippy-bird__hint">Fly through the gaps. One point per pipe. How far can you go?</p>
    </section>
  );
}
